import React, { useState, useEffect, useMemo } from 'react';
import { getAppraisals, getExitInterviews, getPIPs } from '@/services/performanceService';
import { getGoals } from '@/services/goalService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationPageSize,
} from '@/components/ui/pagination';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { CheckCircle2, ChevronDown, ChevronRight, AlertCircle, Clock, Users, Search, Eye, Target, Flag, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

const EMPLOYEE_STEPS = ["Self Review", "Approval Review", "Employee Comment", "Admin Review", "Completed"];

const stepIndex = (status) => {
  const i = EMPLOYEE_STEPS.indexOf(status);
  return i >= 0 ? i : 0;
};

const StarDisplay = ({ rating }) => {
  const numericRating = Number(rating || 0);
  const full = Math.floor(numericRating);
  const hasHalf = numericRating % 1 >= 0.3;

  return (
    <div className="flex items-center">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`h-3 w-3 ${i < full
              ? "text-primary fill-primary"
              : i === full && hasHalf
                ? "text-primary fill-primary"
                : "text-muted-foreground"
            }`}
        />
      ))}
      <span className="text-xs font-semibold ml-1">
        {numericRating.toFixed(1)}
      </span>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    "Pending": "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30",
    "Draft": "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-800",
    "Self Review": "border-primary/20 text-primary bg-primary/10 dark:border-primary/80 dark:bg-primary/10 dark:text-primary",
    "Approval Review": "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800",
    "Admin Review": "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
    "Employee Comment": "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
    "Completed": "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
    "Cancelled": "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  };
  return <Badge variant="outline" className={`text-[10px] ${config[status] || "bg-muted text-muted-foreground"}`}>{status}</Badge>;
};

const EmployeeReviews = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const validTabs = ['appraisals', 'pips', 'exit-interviews', 'goals'];
  const initialTab = validTabs.includes(requestedTab)
    ? requestedTab
    : "appraisals";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [pips, setPips] = useState([]);

  const [expandedAppraisalCycleId, setExpandedAppraisalCycleId] = useState(null);
  const [expandedPipId, setExpandedPipId] = useState(null);
  const [exitInterviews, setExitInterviews] = useState([]);
  const [exitInterviewLoading, setExitInterviewLoading] = useState(false);
  const [exitInterviewError, setExitInterviewError] = useState(null);
  const [hasFetchedExitInterviews, setHasFetchedExitInterviews] = useState(false);
  const [expandedExitInterviewId, setExpandedExitInterviewId] = useState(null);
  const [goals, setGoals] = useState([]);
  const navigate = useNavigate();

  // Appraisal search and filter state
  const [appraisalSearch, setAppraisalSearch] = useState('');
  const [appraisalStatusFilter, setAppraisalStatusFilter] = useState('all');
  const [appraisalCurrentPage, setAppraisalCurrentPage] = useState(1);
  const [appraisalPageSize, setAppraisalPageSize] = useState(5);

  // PIP search and filter state
  const [pipSearch, setPipSearch] = useState('');
  const [pipStatusFilter, setPipStatusFilter] = useState('all');
  const [pipCurrentPage, setPipCurrentPage] = useState(1);
  const [pipPageSize, setPipPageSize] = useState(5);

  // Exit Interview search and filter state
  const [exitInterviewSearch, setExitInterviewSearch] = useState('');
  const [exitInterviewStatusFilter, setExitInterviewStatusFilter] = useState('all');
  const [exitInterviewCurrentPage, setExitInterviewCurrentPage] = useState(1);
  const [exitInterviewPageSize, setExitInterviewPageSize] = useState(5);

  const [goalSearch, setGoalSearch] = useState('');
  const [goalStatusFilter, setGoalStatusFilter] = useState('all');
  const [goalCurrentPage, setGoalCurrentPage] = useState(1);
  const [goalPageSize, setGoalPageSize] = useState(5);

  const handleAppraisalPageSizeChange = (value) => { setAppraisalPageSize(Number(value)); setAppraisalCurrentPage(1); };
  const handlePipPageSizeChange = (value) => { setPipPageSize(Number(value)); setPipCurrentPage(1); };
  const handleExitInterviewPageSizeChange = (value) => { setExitInterviewPageSize(Number(value)); setExitInterviewCurrentPage(1); };
  const handleGoalPageSizeChange = (value) => { setGoalPageSize(Number(value)); setGoalCurrentPage(1); };

  useEffect(() => {
    fetchMyAppraisals();
  }, []);

  const fetchMyAppraisals = async () => {
    try {
      setLoading(true);
      
      const appraisalResponse = await getAppraisals();
      const appraisalData = appraisalResponse.data || [];

      const mappedAppraisals = appraisalData.map((appraisal) => ({
        id: appraisal._id,
        reviewName: appraisal.cycle_name || "One-Time Review",
        managerRating: appraisal.manager_review?.overall_rating || 0,
        employeeId: appraisal.employee_id,
        employeeName: appraisal.employee_name,
        status: appraisal.status,
        template: appraisal.template_id,
        due_date: appraisal.due_date,
        assigned_at: appraisal.createdAt,
      }));

      setAppraisals(mappedAppraisals);
      setExpandedAppraisalCycleId(null);
      
      // Fetch PIPs
      const pipResponse = await getPIPs();
      const pipData = pipResponse.data || [];
      setPips(pipData);
      setExpandedPipId(null);

      const goalResponse = await getGoals();
      const goalData = goalResponse.data || [];
      setGoals(Array.isArray(goalData) ? goalData : []);

    } catch (error) {
      console.error("Error fetching appraisals:", error);
      toast.error("Failed to load appraisal data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyExitInterviews = async () => {
    try {
      setExitInterviewLoading(true);
      setExitInterviewError(null);

      const exitInterviewResponse = await getExitInterviews();
      setExitInterviews(exitInterviewResponse.data || []);
      setExpandedExitInterviewId(null);
      setHasFetchedExitInterviews(true);
    } catch (error) {
      console.error("Error fetching exit interviews:", error);
      setExitInterviewError("Failed to load exit interview data");
      toast.error("Failed to load exit interview data");
    } finally {
      setExitInterviewLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    const shouldUseDefaultPath = value === "appraisals";
    setSearchParams(shouldUseDefaultPath ? {} : { tab: value });

    if (
      value === 'exit-interviews' &&
      !hasFetchedExitInterviews &&
      !exitInterviewLoading
    ) {
      fetchMyExitInterviews();
    }
  };

  const toggleAppraisalExpand = (cycleId) => {
    setExpandedAppraisalCycleId(prev => prev === cycleId ? null : cycleId);
  };

  const togglePIPExpand = (pipId) => {
    setExpandedPipId(prev => prev === pipId ? null : pipId);
  };

  const toggleExitInterviewExpand = (exitInterviewId) => {
    setExpandedExitInterviewId(prev => prev === exitInterviewId ? null : exitInterviewId);
  };

  const handleAppraisalClick = (appraisalId) => {
    navigate(`/employee/review/${appraisalId}`, {
      state: { returnTo: "/employee/reviews" },
    });
  };

  const handleGoalClick = (goalId) => {
  navigate(`/employee/goal/${goalId}`, {
    state: { returnTo: '/employee/reviews?tab=goals' },
  });
};

const getGoalId = (goal) => goal._id || goal.id;

const getGoalProgress = (goal) => {
  const directProgress = Number(goal.progress);

  if (!Number.isNaN(directProgress)) {
    return Math.min(Math.max(directProgress, 0), 100);
  }

  const keyResults = Array.isArray(goal.keyResults) ? goal.keyResults : [];

  if (keyResults.length === 0) return 0;

  const totalProgress = keyResults.reduce((sum, keyResult) => {
    const value = Number(keyResult.progress ?? keyResult.current ?? 0);
    return sum + (Number.isNaN(value) ? 0 : value);
  }, 0);

  return Math.round(totalProgress / keyResults.length);
};

const getCompletedKeyResults = (goal) => {
  const keyResults = Array.isArray(goal.keyResults) ? goal.keyResults : [];

  return keyResults.filter((keyResult) => {
    const progress = Number(keyResult.progress ?? keyResult.current ?? 0);
    return !Number.isNaN(progress) && progress >= 100;
  }).length;
};

const isGoalOverdue = (goal) => {
  if (goal.status === 'Completed') return false;

  const dueDateValue = goal.dueDate || goal.endDate;
  if (!dueDateValue) return false;

  const dueDate = new Date(dueDateValue);
  if (Number.isNaN(dueDate.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);

  return dueDate < today;
};

  useEffect(() => {
    const nextTab = validTabs.includes(requestedTab)
      ? requestedTab
      : "appraisals";

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, requestedTab]);

  useEffect(() => {
    if (
      activeTab === 'exit-interviews' &&
      !hasFetchedExitInterviews &&
      !exitInterviewLoading
    ) {
      fetchMyExitInterviews();
    }
  }, [activeTab, hasFetchedExitInterviews, exitInterviewLoading]);

  // Get unique departments from appraisals
  const appraisalDepartments = useMemo(() => {
    const deps = new Set();
    appraisals.forEach(cycle => {
      if (cycle.department && cycle.department !== 'N/A') {
        deps.add(cycle.department);
      }
    });
    return Array.from(deps);
  }, [appraisals]);

  // Get unique departments from PIPs
  const pipDepartments = useMemo(() => {
    const deps = new Set();
    pips.forEach(pip => {
      if (pip.department && pip.department !== 'N/A') {
        deps.add(pip.department);
      }
    });
    return Array.from(deps);
  }, [pips]);

  // Get unique departments from exit interviews
  const exitInterviewDepartments = useMemo(() => {
    const deps = new Set();
    exitInterviews.forEach(ei => {
      if (ei.department && ei.department !== 'N/A') {
        deps.add(ei.department);
      }
    });
    return Array.from(deps);
  }, [exitInterviews]);

  const getAppraisalProgress = (status) => {
    if(status === "Cancelled") return 0;
    const idx = EMPLOYEE_STEPS.indexOf(status);
    const max = EMPLOYEE_STEPS.length - 1;
    return idx >= 0 && max > 0 ? Math.round((idx / max) * 100) : 0;
  };

  const getAppraisalGroupSummary = (appraisalList = []) =>{
    const completed = appraisalList.filter(a => a.status === "Completed").length;
    const cancelled = appraisalList.filter(a => a.status === "Cancelled").length;
    const active = appraisalList.length - completed - cancelled;
    return { completed, active, cancelled };
  
    let status = "Pending";
    if(appraisalList.length === 0) status = "Pending";
    else if(completed === appraisalList.length) {
      status = "Completed";
    } else if(cancelled === appraisalList.length){
      status = "Cancelled";
    } else if (active > 0){
      status = "In Progress";
    }
    return { completed, active, cancelled, total: appraisalList.length, status };
  }

  // Calculate cycle progress
  const getCycleProgress = (cycle) => {
    const appraisalList = cycle.appraisals || [];
    if (appraisalList.length === 0) return 0;
    const totalSteps = appraisalList.length * (EMPLOYEE_STEPS.length - 1);
    const completedSteps = appraisalList.reduce((sum, a) => {
      const idx = EMPLOYEE_STEPS.indexOf(a.status);
      return sum + (idx >= 0 ? idx : 0);
    }, 0);
    return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  };

// Filter and paginate appraisals
const filteredAppraisals = useMemo(() => {
  return appraisals.filter((appraisal) => {
    if (appraisalSearch) {
      const search = appraisalSearch.toLowerCase();

      if (
        !(appraisal.reviewName || "").toLowerCase().includes(search) &&
        !(appraisal.department || "").toLowerCase().includes(search) &&
        !(appraisal.status || "").toLowerCase().includes(search)
      ) {
        return false;
      }
    }

    if (appraisalStatusFilter !== "all" && appraisal.status !== appraisalStatusFilter) {
      return false;
    }

    return true;
  });
}, [appraisals, appraisalSearch, appraisalStatusFilter]);

const totalAppraisalPages = Math.ceil(filteredAppraisals.length / appraisalPageSize);
const paginatedAppraisals = filteredAppraisals.slice(
  (appraisalCurrentPage - 1) * appraisalPageSize,
  appraisalCurrentPage * appraisalPageSize
);


  // Filter and paginate PIPs
  const filteredPips = useMemo(() => {
    return pips.filter(pip => {
      // Search filter
      if (pipSearch) {
        const search = pipSearch.toLowerCase();

        const milestoneLabels = (pip.pip_milestones || []).map((m) => m.label || "").join(" ").toLowerCase();

        if (
          !(pip.employee_name || '').toLowerCase().includes(search) &&
          !(pip.department || '').toLowerCase().includes(search) &&
          !(pip.status || '').toLowerCase().includes(search) &&
          !(pip.pip_reason || '').toLowerCase().includes(search) &&
          !milestoneLabels.includes(search)
        ) {
          return false;
        }
      }

      // Status filter
      if (pipStatusFilter !== 'all' && pip.status !== pipStatusFilter) {
        return false;
      }

      return true;
    });
  }, [pips, pipSearch, pipStatusFilter]);

  const totalPipPages = Math.ceil(filteredPips.length / pipPageSize);
  const paginatedPips = filteredPips.slice(
    (pipCurrentPage - 1) * pipPageSize,
    pipCurrentPage * pipPageSize
  );

  // Filter and paginate exit interviews
  const filteredExitInterviews = useMemo(() => {
    return exitInterviews.filter(ei => {
      // Search filter
      if (exitInterviewSearch) {
        const search = exitInterviewSearch.toLowerCase();
        if (
          !(ei.employee_name || '').toLowerCase().includes(search) &&
          !(ei.department || '').toLowerCase().includes(search) &&
          !(ei.status || '').toLowerCase().includes(search)
        ) {
          return false;
        }
      }

      // Status filter
      if (exitInterviewStatusFilter !== 'all' && ei.status !== exitInterviewStatusFilter) {
        return false;
      }

      return true;
    });
  }, [exitInterviews, exitInterviewSearch, exitInterviewStatusFilter]);

  const totalExitInterviewPages = Math.ceil(filteredExitInterviews.length / exitInterviewPageSize);
  const paginatedExitInterviews = filteredExitInterviews.slice(
    (exitInterviewCurrentPage - 1) * exitInterviewPageSize,
    exitInterviewCurrentPage * exitInterviewPageSize
  );

  const filteredGoals = useMemo(() => {
  return goals.filter((goal) => {
    if (goalSearch) {
      const search = goalSearch.toLowerCase();

      const hasSearchMatch =
        (goal.title || '').toLowerCase().includes(search) ||
        (goal.description || '').toLowerCase().includes(search) ||
        (goal.project || '').toLowerCase().includes(search) ||
        (goal.department || '').toLowerCase().includes(search) ||
        (goal.status || '').toLowerCase().includes(search) ||
        (goal.priority || '').toLowerCase().includes(search);

      if (!hasSearchMatch) return false;
    }

    if (goalStatusFilter !== 'all' && goal.status !== goalStatusFilter) {
      return false;
    }

    return true;
  });
}, [goals, goalSearch, goalStatusFilter]);

const totalGoalPages = Math.ceil(filteredGoals.length / goalPageSize);

const paginatedGoals = filteredGoals.slice(
  (goalCurrentPage - 1) * goalPageSize,
  goalCurrentPage * goalPageSize
);

  // Reset to page 1 when filters change
  useEffect(() => {
    setAppraisalCurrentPage(1);
  }, [appraisalSearch, appraisalStatusFilter]);

  useEffect(() => {
    setPipCurrentPage(1);
  }, [pipSearch, pipStatusFilter]);

  useEffect(() => {
    setExitInterviewCurrentPage(1);
  }, [exitInterviewSearch, exitInterviewStatusFilter]);

  useEffect(() => {
    setGoalCurrentPage(1);
  }, [goalSearch, goalStatusFilter]);

  // Pagination helpers
  const renderPageNumbers = (currentPage, totalPages, onPageChange) => {
    return [1, ...Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
      const start = Math.max(2, Math.min(currentPage - 2, totalPages - 4));
      return start + i;
    }).filter(p => p > 1 && p < totalPages), totalPages]
      .filter((p, i, arr) => p && arr.indexOf(p) === i)
      .reduce((acc, page, index, arr) => {
        if (index === 0) return [page];
        const prev = arr[index - 1];
        if (page - prev === 2) return [...acc, prev + 1, page];
        if (page - prev > 1) return [...acc, '...', page];
        return [...acc, page];
      }, [])
      .map((page, index) => (
        <PaginationItem key={index}>
          {page === '...' ? (
            <span className="px-2 text-muted-foreground">...</span>
          ) : (
            <PaginationLink
              isActive={page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PaginationLink>
          )}
        </PaginationItem>
      ));
  };

  const renderAppraisalPagination = () => {
    if (totalAppraisalPages <= 1) return null;

    return (
      <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
        <div className="flex items-center gap-4">
          <PaginationPageSize value={appraisalPageSize} onValueChange={handleAppraisalPageSizeChange} />
          <div className="text-sm text-muted-foreground text-center sm:text-left whitespace-nowrap">
            Showing {Math.min((appraisalCurrentPage - 1) * appraisalPageSize + 1, filteredAppraisals.length)} to {Math.min(appraisalCurrentPage * appraisalPageSize, filteredAppraisals.length)} of {filteredAppraisals.length} entries

          </div>
        </div>
        <Pagination className="lg:!justify-end justify-center">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setAppraisalCurrentPage(Math.max(1, appraisalCurrentPage - 1))}
                className={appraisalCurrentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            {renderPageNumbers(appraisalCurrentPage, totalAppraisalPages, setAppraisalCurrentPage)}
            <PaginationItem>
              <PaginationNext
                onClick={() => setAppraisalCurrentPage(Math.min(totalAppraisalPages, appraisalCurrentPage + 1))}
                className={appraisalCurrentPage >= totalAppraisalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  const renderPipPagination = () => {
    if (totalPipPages <= 1) return null;

    return (
      <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
        <div className="flex items-center gap-4">
          <PaginationPageSize value={pipPageSize} onValueChange={handlePipPageSizeChange} />
          <div className="text-sm text-muted-foreground text-center sm:text-left whitespace-nowrap">
            Showing {Math.min((pipCurrentPage - 1) * pipPageSize + 1, filteredPips.length)} to {Math.min(pipCurrentPage * pipPageSize, filteredPips.length)} of {filteredPips.length} entries
          </div>
        </div>
        <Pagination className="!justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setPipCurrentPage(Math.max(1, pipCurrentPage - 1))}
                className={pipCurrentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            {renderPageNumbers(pipCurrentPage, totalPipPages, setPipCurrentPage)}
            <PaginationItem>
              <PaginationNext
                onClick={() => setPipCurrentPage(Math.min(totalPipPages, pipCurrentPage + 1))}
                className={pipCurrentPage >= totalPipPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  const renderExitInterviewPagination = () => {
    if (totalExitInterviewPages <= 1) return null;

    return (
      <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
        <div className="flex items-center gap-4">
          <PaginationPageSize value={exitInterviewPageSize} onValueChange={handleExitInterviewPageSizeChange} />
          <div className="text-sm text-muted-foreground text-center sm:text-left whitespace-nowrap">
            Showing {Math.min((exitInterviewCurrentPage - 1) * exitInterviewPageSize + 1, filteredExitInterviews.length)} to {Math.min(exitInterviewCurrentPage * exitInterviewPageSize, filteredExitInterviews.length)} of {filteredExitInterviews.length} entries
          </div>
        </div>
        <Pagination className="!justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setExitInterviewCurrentPage(Math.max(1, exitInterviewCurrentPage - 1))}
                className={exitInterviewCurrentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            {renderPageNumbers(exitInterviewCurrentPage, totalExitInterviewPages, setExitInterviewCurrentPage)}
            <PaginationItem>
              <PaginationNext
                onClick={() => setExitInterviewCurrentPage(Math.min(totalExitInterviewPages, exitInterviewCurrentPage + 1))}
                className={exitInterviewCurrentPage >= totalExitInterviewPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  const renderGoalPagination = () => {
  if (totalGoalPages <= 1) return null;

  return (
    <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
      <div className="flex items-center gap-4">
        <PaginationPageSize value={goalPageSize} onValueChange={handleGoalPageSizeChange} />
        <div className="text-sm text-muted-foreground text-center sm:text-left whitespace-nowrap">
          Showing {Math.min((goalCurrentPage - 1) * goalPageSize + 1, filteredGoals.length)} to {Math.min(goalCurrentPage * goalPageSize, filteredGoals.length)} of {filteredGoals.length} entries
        </div>
      </div>

      <Pagination className="!justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => setGoalCurrentPage(Math.max(1, goalCurrentPage - 1))}
              className={goalCurrentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>

          {renderPageNumbers(goalCurrentPage, totalGoalPages, setGoalCurrentPage)}

          <PaginationItem>
            <PaginationNext
              onClick={() => setGoalCurrentPage(Math.min(totalGoalPages, goalCurrentPage + 1))}
              className={goalCurrentPage >= totalGoalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

  const getProgressPercentage = (status) => {
    const idx = stepIndex(status);
    if (status === 'Completed') return 100;
    return Math.round((idx / (EMPLOYEE_STEPS.length - 1)) * 100);
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getExitInterviewDate = (exitInterview) => (
    exitInterview.due_date || 'N/A'
  );

  return (
    <>
      <div className="flex flex-col md:items-start mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Performance Management
        </h1>
        <p className="text-gray-500">
          Track your appraisal cycles, goals, performance improvement plans and
          exit interviews.
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <TabsList className="flex justify-start space-x-8 overflow-x-auto overflow-y-hidden touch-pan-x bg-transparent h-auto p-0 no-scrollbar">
          <TabsTrigger
            value="appraisals"
            className="w-fit px-4 text-left sm:w-auto"
          >
            Appraisals
          </TabsTrigger>
          <TabsTrigger value="pips" className="w-fit px-4 text-left sm:w-auto">
            Performance Improvement Plans
          </TabsTrigger>
          <TabsTrigger
            value="exit-interviews"
            className="w-fit px-4 text-left sm:w-auto"
          >
            Exit Interviews
          </TabsTrigger>
          <TabsTrigger value="goals" className="w-fit px-4 text-left sm:w-auto">
            Goals
          </TabsTrigger>
        </TabsList>

        {/* Appraisals Tab */}
        <TabsContent value="appraisals" className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading appraisals...
              </CardContent>
            </Card>
          ) : appraisals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No appraisals found. Your manager will assign appraisals to you.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Your Appraisals</CardTitle>
                <CardDescription>
                  Track your assigned performance reviews.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by review name, department..."
                        value={appraisalSearch}
                        onChange={(e) => setAppraisalSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={appraisalStatusFilter}
                      onValueChange={setAppraisalStatusFilter}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Self Review">Self Review</SelectItem>
                        <SelectItem value="Approval Review">
                          Approval Review
                        </SelectItem>
                        <SelectItem value="Employee Comment">
                          Employee Comment
                        </SelectItem>
                        <SelectItem value="Admin Review">
                          Admin Review
                        </SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div
                  className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar"
                  style={{ scrollbarWidth: "thin" }}
                >
                  <div
                    className="min-w-[800px] thin-scrollbar"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-gray-200 dark:border-gray-700">
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Review Name
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Rating
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Due Date
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Progress
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Status
                          </TableHead>
                          <TableHead className="text-right text-gray-700 dark:text-gray-300">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedAppraisals.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-center py-6 md:py-8 text-muted-foreground"
                            >
                              <div className="flex flex-col items-center space-y-2 px-4">
                                <Users className="h-6 w-6 md:h-8 md:w-8 text-gray-400" />
                                <p className="text-sm md:text-base">
                                  No appraisals found matching your filters
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setAppraisalSearch("");
                                    setAppraisalStatusFilter("all");
                                  }}
                                >
                                  Clear filters
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedAppraisals.map((appraisal) => {
                            const progress = getAppraisalProgress(
                              appraisal.status,
                            );

                            return (
                              <TableRow
                                key={appraisal.id}
                                className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                                data-testid={`appraisal-${appraisal.id}`}
                                onClick={() =>
                                  handleAppraisalClick(appraisal.id)
                                }
                              >
                                <TableCell>
                                  <div className="font-medium text-gray-900 dark:text-gray-200">
                                    {appraisal.reviewName}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {appraisal.managerRating > 0 ? (
                                    <StarDisplay
                                      rating={appraisal.managerRating}
                                    />
                                  ) : (
                                    <span className="text-xs text-muted-foreground">
                                      Not rated
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {appraisal.due_date
                                    ? new Date(
                                        appraisal.due_date,
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                    : "No deadline"}
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-muted-foreground">
                                        {progress}%
                                      </span>
                                    </div>
                                    <Progress
                                      value={progress}
                                      className="h-2"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <StatusBadge status={appraisal.status} />
                                </TableCell>
                                <TableCell
                                  className="text-right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                    onClick={() =>
                                      handleAppraisalClick(appraisal.id)
                                    }
                                    title="View Review"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                {/* Pagination */}
                {renderAppraisalPagination()}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* PIPs Tab */}
        <TabsContent value="pips" className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading PIPs...
              </CardContent>
            </Card>
          ) : pips.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No active Performance Improvement Plans found.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Your Performance Improvement Plans</CardTitle>
                <CardDescription>
                  Monitor your ongoing PIPs and their progress.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by status, reason..."
                        value={pipSearch}
                        onChange={(e) => setPipSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={pipStatusFilter}
                      onValueChange={setPipStatusFilter}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Draft">Pending</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div
                  className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar"
                  style={{ scrollbarWidth: "thin" }}
                >
                  <div
                    className="min-w-[800px] thin-scrollbar"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-gray-200 dark:border-gray-700">
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Duration
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Milestones
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Progress
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Status
                          </TableHead>
                          <TableHead className="text-right text-gray-700 dark:text-gray-300">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedPips.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-center py-6 md:py-8 text-muted-foreground"
                            >
                              <div className="flex flex-col items-center space-y-2 px-4">
                                <Users className="h-6 w-6 md:h-8 md:w-8 text-gray-400" />
                                <p className="text-sm md:text-base">
                                  No PIPs found matching your filters
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setPipSearch("");
                                    setPipStatusFilter("all");
                                  }}
                                >
                                  Clear filters
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedPips.map((pip) => {
                            const isCompleted = pip.status === "Completed";
                            const completedMilestones = (
                              pip.pip_milestones || []
                            ).filter((m) => m.completed === true).length;
                            const totalMilestones = (pip.pip_milestones || [])
                              .length;
                            const milestonePct =
                              totalMilestones > 0
                                ? Math.round(
                                    (completedMilestones / totalMilestones) *
                                      100,
                                  )
                                : 0;

                            return (
                              <TableRow
                                key={pip._id}
                                className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                                data-testid={`pip-${pip._id}`}
                                onClick={() =>
                                  navigate(`/employee/pip/${pip._id}`, {
                                    state: {
                                      returnTo: "/employee/reviews?tab=pips",
                                    },
                                  })
                                }
                              >
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {new Date(
                                    pip.pip_start_date,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}{" "}
                                  —{" "}
                                  {new Date(
                                    pip.pip_end_date,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </TableCell>
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {completedMilestones}/{totalMilestones}
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-muted-foreground">
                                        {milestonePct}%
                                      </span>
                                      <span className="text-muted-foreground">
                                        {completedMilestones}/{totalMilestones}
                                      </span>
                                    </div>
                                    <Progress
                                      value={milestonePct}
                                      className="h-2"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] ${
                                      pip.status === "Pending" ||
                                      pip.status === "Draft"
                                        ? "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30"
                                        : pip.status === "Completed"
                                          ? "bg-primary text-primary-foreground border-primary dark:bg-primary dark:border-primary"
                                          : pip.status === "Cancelled"
                                            ? "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800"
                                            : "bg-muted text-muted-foreground border-muted-foreground/20"
                                    }`}
                                  >
                                    {pip.status === "Draft"
                                      ? "Pending"
                                      : pip.status}
                                  </Badge>
                                </TableCell>
                                <TableCell
                                  className="text-right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex items-center justify-end space-x-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                      onClick={() =>
                                        navigate(`/employee/pip/${pip._id}`, {
                                          state: {
                                            returnTo:
                                              "/employee/reviews?tab=pips",
                                          },
                                        })
                                      }
                                      title="View Details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>

                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                      onClick={() => togglePIPExpand(pip._id)}
                                      title="Expand/Collapse Milestones"
                                    >
                                      {expandedPipId === pip._id ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Pagination */}
                {renderPipPagination()}

                {/* Expanded Milestones */}
                {expandedPipId &&
                  pips
                    .filter((pip) => pip._id === expandedPipId)
                    .map((pip) => {
                      if (
                        !pip.pip_milestones ||
                        pip.pip_milestones.length === 0
                      )
                        return null;

                      return (
                        <div key={pip._id} className="mt-4">
                          <Separator className="mb-4" />
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-foreground">
                              Milestones
                            </h3>
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                pip.status === "Pending" ||
                                pip.status === "Draft"
                                  ? "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30"
                                  : pip.status === "Completed"
                                    ? "bg-primary text-primary-foreground border-primary dark:bg-primary dark:border-primary"
                                    : "bg-muted text-muted-foreground border-muted-foreground/20"
                              }`}
                            >
                              {pip.status === "Draft" ? "Pending" : pip.status}
                            </Badge>
                          </div>
                          {pip.pip_reason && (
                            <p className="text-sm text-muted-foreground mb-4">
                              {pip.pip_reason}
                            </p>
                          )}
                          <div className="border border-gray-200 dark:border-gray-700 rounded-md">
                            <div
                              className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar"
                              style={{ scrollbarWidth: "thin" }}
                            >
                              <div
                                className="min-w-[800px] thin-scrollbar"
                                style={{ scrollbarWidth: "thin" }}
                              >
                                <Table>
                                  <TableHeader>
                                    <TableRow className="border-b border-gray-200 dark:border-gray-700">
                                      <TableHead className="text-gray-700 dark:text-gray-300 w-12">
                                        #
                                      </TableHead>
                                      <TableHead className="text-gray-700 dark:text-gray-300">
                                        Milestone
                                      </TableHead>
                                      <TableHead className="text-gray-700 dark:text-gray-300">
                                        Target Date
                                      </TableHead>
                                      <TableHead className="text-gray-700 dark:text-gray-300">
                                        Status
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {pip.pip_milestones.map((ms, idx) => {
                                      const isDone =
                                        ms.completed === true ||
                                        ms.status === "Completed";
                                      return (
                                        <TableRow
                                          key={ms.milestone_id || ms.id || idx}
                                          className="border-b border-gray-200 dark:border-gray-700"
                                        >
                                          <TableCell className="text-gray-800 dark:text-gray-300">
                                            <div
                                              className={`h-7 w-7 rounded-full flex items-center justify-center ${isDone ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                                            >
                                              {isDone ? (
                                                <CheckCircle2 className="h-4 w-4" />
                                              ) : (
                                                <span className="text-xs font-semibold">
                                                  {idx + 1}
                                                </span>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="font-medium text-gray-900 dark:text-gray-200">
                                              {ms.label}
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-gray-800 dark:text-gray-300">
                                            {new Date(
                                              ms.date,
                                            ).toLocaleDateString("en-US", {
                                              month: "short",
                                              day: "numeric",
                                              year: "numeric",
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            <Badge
                                              variant="outline"
                                              className={`text-[10px] ${
                                                isDone
                                                  ? "bg-primary text-primary-foreground border-primary dark:bg-primary dark:border-primary"
                                                  : "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30"
                                              }`}
                                            >
                                              {isDone ? "Completed" : "Pending"}
                                            </Badge>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Exit Interviews Tab */}
        <TabsContent value="exit-interviews" className="space-y-4">
          {exitInterviewLoading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading exit interviews...
              </CardContent>
            </Card>
          ) : exitInterviewError ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <div className="flex flex-col items-center space-y-3">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <p>{exitInterviewError}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchMyExitInterviews}
                  >
                    Retry
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : exitInterviews.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No exit interviews found.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Your Exit Interviews</CardTitle>
                <CardDescription>
                  Track and manage your exit interviews.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by department or status..."
                        value={exitInterviewSearch}
                        onChange={(e) => setExitInterviewSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={exitInterviewStatusFilter}
                      onValueChange={setExitInterviewStatusFilter}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Approval Review">
                          Approval Review
                        </SelectItem>
                        <SelectItem value="Admin Review">
                          Admin Review
                        </SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div
                  className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar"
                  style={{ scrollbarWidth: "thin" }}
                >
                  <div
                    className="min-w-[800px] thin-scrollbar"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-gray-200 dark:border-gray-700">
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Department
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Status
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Due Date
                          </TableHead>
                          <TableHead className="text-right text-gray-700 dark:text-gray-300">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedExitInterviews.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-center py-6 md:py-8 text-muted-foreground"
                            >
                              <div className="flex flex-col items-center space-y-2 px-4">
                                <Users className="h-6 w-6 md:h-8 md:w-8 text-gray-400" />
                                <p className="text-sm md:text-base">
                                  No exit interviews found matching your filters
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setExitInterviewSearch("");
                                    setExitInterviewStatusFilter("all");
                                  }}
                                >
                                  Clear filters
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedExitInterviews.map((ei) => {
                            return (
                              <TableRow
                                key={ei._id}
                                className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                                data-testid={`exit-interview-${ei._id}`}
                                onClick={() =>
                                  navigate(
                                    `/employee/exit-interview/${ei._id}`,
                                    {
                                      state: {
                                        returnTo:
                                          "/employee/reviews?tab=exit-interviews",
                                      },
                                    },
                                  )
                                }
                              >
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {ei.department || "N/A"}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] 
                                  ${
                                    ei.status === "Completed"
                                      ? "bg-primary text-primary-foreground border-primary dark:bg-primary dark:border-primary"
                                      : "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30"
                                  }`}
                                  >
                                    {ei.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-gray-800 dark:text-gray-300">
                                  {formatDate(getExitInterviewDate(ei))}
                                </TableCell>
                                <TableCell
                                  className="text-right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex items-center justify-end space-x-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                      onClick={() =>
                                        navigate(
                                          `/employee/exit-interview/${ei._id}`,
                                          {
                                            state: {
                                              returnTo:
                                                "/employee/reviews?tab=exit-interviews",
                                            },
                                          },
                                        )
                                      }
                                      title="View Details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>

                                    {/* <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                  onClick={() => toggleExitInterviewExpand(ei._id)}
                                  title="Expand/Collapse Details"
                                  >
                                    {expandedExitInterviewId === ei._id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </Button> */}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Pagination */}
                {renderExitInterviewPagination()}

                {/* Expanded Exit Interview Details */}
                {/* {expandedExitInterviewId && exitInterviews.filter(ei => ei._id === expandedExitInterviewId).map((ei) => {
                  return (
                    <div key={ei._id} className="mt-4">
                      <Separator className="mb-4" />
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground">Exit Interview Details</h3>
                        <Badge variant="outline" className={`text-[10px] 
                          ${ei.status === 'Completed' ? 'bg-primary text-primary-foreground border-primary dark:bg-primary dark:border-primary' : 
                          'bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30'}`
                          }>
                            {ei.status}
                            </Badge>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Department</p>
                            <p className="text-sm font-medium text-foreground">{ei.department || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Last Working Day</p>
                            <p className="text-sm font-medium text-foreground">{formatDate(ei.employee_id?.last_working_date || 'N/A')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Due Date</p>
                            <p className="text-sm font-medium text-foreground">{formatDate(getExitInterviewDate(ei))}</p>
                          </div>
                        </div>
                        {ei.reason_for_leaving && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Reason for Leaving</p>
                            <p className="text-sm text-foreground">{ei.reason_for_leaving}</p>
                          </div>
                        )}
                        {ei.feedback && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Feedback</p>
                            <p className="text-sm text-foreground">{ei.feedback}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })} */}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="goals" className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading goals...
              </CardContent>
            </Card>
          ) : goals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No goals found. Your manager will assign goals to you.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Your Assigned Goals</CardTitle>
                <CardDescription>
                  View your assigned goals, progress, priorities, and key
                  results.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by goal, project..."
                        value={goalSearch}
                        onChange={(event) => setGoalSearch(event.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Select
                      value={goalStatusFilter}
                      onValueChange={setGoalStatusFilter}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="On Track">On Track</SelectItem>
                        <SelectItem value="At Risk">At Risk</SelectItem>
                        <SelectItem value="Behind">Behind</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div
                  className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar"
                  style={{ scrollbarWidth: "thin" }}
                >
                  <div
                    className="min-w-[900px]"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-gray-200 dark:border-gray-700">
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Goal
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Project
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Priority
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Status
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Progress
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Key Results
                          </TableHead>
                          <TableHead className="text-gray-700 dark:text-gray-300">
                            Due Date
                          </TableHead>
                          <TableHead className="text-right text-gray-700 dark:text-gray-300">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>

                      <TableBody>
                        {paginatedGoals.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="text-center py-8 text-muted-foreground"
                            >
                              <div className="flex flex-col items-center space-y-2 px-4">
                                <Target className="h-8 w-8 text-gray-400" />
                                <p>No goals found matching your filters</p>

                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setGoalSearch("");
                                    setGoalStatusFilter("all");
                                  }}
                                >
                                  Clear filters
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedGoals.map((goal) => {
                            const goalId = getGoalId(goal);
                            const progress = getGoalProgress(goal);
                            const keyResults = Array.isArray(goal.keyResults)
                              ? goal.keyResults
                              : [];
                            const completedKeyResults =
                              getCompletedKeyResults(goal);
                            const overdue = isGoalOverdue(goal);

                            return (
                              <TableRow
                                key={goalId}
                                className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                                onClick={() => handleGoalClick(goalId)}
                              >
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="font-medium text-gray-900 dark:text-gray-200 line-clamp-1">
                                      {goal.title || "Untitled Goal"}
                                    </div>
                                  </div>
                                </TableCell>

                                <TableCell className="text-sm text-gray-800 dark:text-gray-300">
                                  {goal.project || "Unassigned"}
                                </TableCell>

                                <TableCell>
                                  <Badge
                                    variant={goal.priority}
                                    className={`text-[10px]
                                    ${
                                      goal.priority === "Critical"
                                        ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
                                        : goal.priority === "High"
                                          ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
                                          : goal.priority === "Medium"
                                            ? "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800"
                                            : goal.priority === "Low"
                                              ? "bg-muted text-muted-foreground border-muted-foreground/20 dark:border-muted-foreground/20"
                                              : "bg-muted text-muted-foreground border-muted-foreground/20 dark:border-muted-foreground/20"
                                    }
                                    `}
                                  >
                                    {goal.priority}
                                  </Badge>
                                </TableCell>

                                <TableCell>
                                  <Badge
                                    variant={goal.status}
                                    className={`text-[10px]
                                    ${
                                      goal.status === "Completed"
                                        ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                                        : goal.status === "On Track"
                                          ? "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800"
                                          : goal.status === "At Risk"
                                            ? "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800"
                                            : goal.status === "Behind"
                                              ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
                                              : "bg-muted text-muted-foreground border-muted-foreground/20 dark:border-muted-foreground/20"
                                    }
                                    `}
                                  >
                                    {goal.status}
                                  </Badge>
                                </TableCell>

                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Progress
                                      value={progress}
                                      className="h-2 flex-1 min-w-[120px]"
                                    />
                                    <span className="text-xs font-semibold min-w-[35px]">
                                      {progress}%
                                    </span>
                                  </div>
                                </TableCell>

                                <TableCell className="text-sm text-gray-800 dark:text-gray-300">
                                  <div className="flex items-center gap-2">
                                    <Flag className="h-4 w-4 text-primary" />
                                    <span>
                                      {completedKeyResults} /{" "}
                                      {keyResults.length}
                                    </span>
                                  </div>
                                </TableCell>

                                <TableCell
                                  className={`text-sm ${
                                    overdue
                                      ? "text-red-600 font-semibold"
                                      : "text-gray-800 dark:text-gray-300"
                                  }`}
                                >
                                  {formatDate(goal.dueDate || goal.endDate)}
                                </TableCell>

                                <TableCell
                                  className="text-right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                    onClick={() => handleGoalClick(goalId)}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {renderGoalPagination()}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </>
  );
};

export default EmployeeReviews;
